import { useParams, useNavigate, useLocation } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useState, useEffect } from 'react'
import { Helmet } from 'react-helmet-async'
import { api } from '@/services/api'
import { getEpornerVideoDetails, parseVideoId, normalizeEpornerVideo } from '@/services/adultContent'
import { type Content } from '@/services/homepage'
import { useAuthStore } from '@/stores/authStore'
import { useSubscription } from '@/hooks/useSubscription'
import SubscriptionModal from '@/components/SubscriptionModal'
import './ContentPage.css'

// Interface pour les vidéos Eporner de la recherche
interface EpornerVideoSearch {
  id?: string
  vid?: string
  title?: string
  keywords?: string
  duration?: string
  views?: number
  thumb?: string
  default_thumb?: string | { src?: string }
}

interface EpornerThumb {
  size: string
  width: number
  height: number
  src: string
}

interface EpornerVideoDetails {
  id: string
  title: string
  keywords: string
  views: number
  rate: string
  url: string
  added: string
  length_sec: number
  length_min: string
  embed: string
  default_thumb: EpornerThumb
  thumbs: EpornerThumb[]
}

const EpornerContentPage = () => {
  const { id } = useParams<{ id: string }>()
  const parsedId = id ? parseVideoId(id) : null
  
  // Authentication et subscription
  const { isAuthenticated } = useAuthStore()
  const { activeSubscription, hasActiveSubscription, loading: subscriptionsLoading } = useSubscription()
  const queryClient = useQueryClient()
  
  // États pour les modales et le suivi
  const [showModal, setShowModal] = useState(false)
  const [modalType, setModalType] = useState<'no-subscription' | 'limit-reached' | 'upgrade-required'>('no-subscription')
  const [videoCount, setVideoCount] = useState(0)
  const [maxVideos, setMaxVideos] = useState<number | null>(null)
  const [hasTrackedView, setHasTrackedView] = useState(false)

  // Requête pour récupérer les détails de la vidéo
  const { data: videoData, isLoading, error } = useQuery({
    queryKey: ['eporner-video', parsedId?.videoId],
    queryFn: async () => {
      if (!parsedId?.videoId) throw new Error('ID de vidéo manquant')
      return await getEpornerVideoDetails(parsedId.videoId) as EpornerVideoDetails
    },
    enabled: !!parsedId?.videoId && !!parsedId,
  })
  
  // Récupérer le nombre de vues d'aujourd'hui
  const { data: viewData } = useQuery({
    queryKey: ['video-view-count'],
    queryFn: async () => {
      if (!isAuthenticated) return { todayCount: 0, limits: { maxViews: 0, hasAccess: false, accessLevel: null } }
      const response = await api.get('/video-access/count')
      return response.data
    },
    enabled: isAuthenticated,
  })
  
  // Vérifier l'accès et afficher les modales appropriées
  useEffect(() => {
    // Attendre que les abonnements soient chargés avant de vérifier
    if (subscriptionsLoading) {
      return
    }
    
    if (!isAuthenticated) {
      // Utilisateur non connecté : afficher la modal après 5 secondes
      setModalType('no-subscription')
      const initialTimer = setTimeout(() => setShowModal(true), 5000)
      // Programmer la réouverture après 30 secondes
      const repeatTimer = setTimeout(() => setShowModal(true), 30000)
      return () => {
        clearTimeout(initialTimer)
        clearTimeout(repeatTimer)
      }
    }
    
    if (!hasActiveSubscription()) {
      // Utilisateur connecté mais sans abonnement : afficher après 5 secondes
      setModalType('no-subscription')
      const initialTimer = setTimeout(() => setShowModal(true), 5000)
      const repeatTimer = setTimeout(() => setShowModal(true), 30000)
      return () => {
        clearTimeout(initialTimer)
        clearTimeout(repeatTimer)
      }
    }
    
    // Utilisateur avec abonnement : vérifier les limites
    if (viewData) {
      setVideoCount(viewData.todayCount)
      setMaxVideos(viewData.limits.maxViews)
      
      // VIP : pas de limite
      if (viewData.limits.accessLevel === 'vip') {
        return
      }
      
      // Basic ou Premium : vérifier les limites
      // Seulement si la limite est atteinte (>= maxViews)
      if (viewData.limits.maxViews !== null && viewData.todayCount >= viewData.limits.maxViews) {
        setModalType('limit-reached')
        setShowModal(true)
      }
    }
  }, [isAuthenticated, hasActiveSubscription, viewData, subscriptionsLoading])
  
  // Enregistrer une vue quand la vidéo est chargée
  useEffect(() => {
    if (videoData && isAuthenticated && parsedId?.videoId && !hasTrackedView) {
      const trackView = async () => {
        try {
          await api.post('/video-access/track', {
            videoId: parsedId.videoId,
            provider: 'eporner',
          })
          setHasTrackedView(true)
          // Réinvalider le cache pour obtenir le nouveau décompte
          await queryClient.invalidateQueries({ queryKey: ['video-view-count'] })
        } catch (error) {
          console.error('Erreur lors de l\'enregistrement de la vue:', error)
        }
      }
      trackView()
    }
  }, [videoData, isAuthenticated, parsedId?.videoId, hasTrackedView, queryClient])

  // Formater la date
  const formatDate = (dateString: string): string => {
    try {
      const date = new Date(dateString)
      return date.toLocaleDateString('fr-FR', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    } catch {
      return dateString
    }
  }

  // Extraire les tags depuis keywords
  const getTags = (): string[] => {
    if (!videoData?.keywords) return []
    return videoData.keywords.split(',').map(tag => tag.trim()).filter(Boolean)
  }

  const tags = videoData ? getTags() : []

  // Requête pour récupérer les vidéos suggérées basées sur les tags
  const { data: suggestedVideos, isLoading: isSuggestedLoading } = useQuery({
    queryKey: ['eporner-suggested', tags],
    queryFn: async () => {
      if (!tags || tags.length === 0) return []
      
      // Prendre les 3 premiers tags pour la recherche
      const searchQuery = tags.slice(0, 3).join(' ')
      
      try {
        const response = await api.get('/adult-content/eporner/search', {
          params: {
            query: searchQuery,
            per_page: 12,
            page: 1,
            order: 'latest',
          },
        })
        
        const videos = response.data?.videos || response.data?.data || []
        // Filtrer la vidéo actuelle et normaliser les résultats
        return videos
          .filter((video: EpornerVideoSearch) => {
            const videoId = video.id || video.vid
            return videoId !== videoData?.id
          })
          .map((video: EpornerVideoSearch, index: number) => {
            // Normaliser au format attendu par normalizeEpornerVideo
            const normalizedVideo = {
              id: video.id || video.vid,
              vid: video.vid || video.id,
              title: video.title,
              keywords: video.keywords,
              duration: video.duration,
              views: video.views,
              thumb: video.thumb,
              default_thumb: typeof video.default_thumb === 'string' 
                ? video.default_thumb 
                : video.default_thumb?.src,
            }
            return normalizeEpornerVideo(normalizedVideo as any, index)
          })
          .filter((video: Content | null): video is Content => video !== null)
          .slice(0, 12)
      } catch (error) {
        console.error('Erreur lors de la récupération des vidéos suggérées:', error)
        return []
      }
    },
    enabled: !!videoData && tags.length > 0,
  })

  // Navigation vers une vidéo suggérée
  const navigate = useNavigate()
  const handleSuggestedVideoClick = (video: Content) => {
    // Extraire l'ID depuis video.id (format: eporner_xxx) ou utiliser providerVideoId
    const videoId = video.providerVideoId || 
                   (typeof video.id === 'string' ? video.id.replace('eporner_', '') : String(video.id)) ||
                   String(video.id)
    navigate(`/content/eporner_${videoId}`)
  }

  // Extraire l'URL src depuis embed
  const getEmbedSrc = (): string | null => {
    if (!videoData?.embed) return null
    // L'embed peut être une URL complète ou un code embed HTML
    if (videoData.embed.startsWith('http://') || videoData.embed.startsWith('https://')) {
      return videoData.embed
    }
    // Si c'est du code HTML, extraire l'URL src
    const srcMatch = videoData.embed.match(/src=["']([^"']+)["']/i)
    return srcMatch && srcMatch[1] ? srcMatch[1] : null
  }

  if (isLoading) {
    return <div className="content-page"><div className="content-page-loading">Chargement...</div></div>
  }

  if (error) {
    console.error('Erreur lors du chargement de la vidéo Eporner:', error)
    return (
      <div className="content-page">
        <div className="content-page-error">
          Erreur lors du chargement de la vidéo
        </div>
      </div>
    )
  }

  if (!videoData) {
    return (
      <div className="content-page">
        <div className="content-page-error">
          Contenu non trouvé
        </div>
      </div>
    )
  }

  const embedSrc = getEmbedSrc()
  const modalRedirectContext = videoData
    ? {
        from: 'video',
        videoId: id,
        videoTitle: videoData.title,
        videoThumbnail: videoData.default_thumb?.src || videoData.thumbs?.[0]?.src,
        videoProvider: parsedId?.provider,
      }
    : {
        from: 'video',
        videoId: id,
        videoProvider: parsedId?.provider,
      }

  const location = useLocation()
  const pageTitle = `${videoData.title} - Eporner - EdenPlaisir.com`
  const pageDescription = `Regardez ${videoData.title} sur EdenPlaisir.com`
  const canonicalUrl = `https://www.edenplaisir.com${location.pathname}`.replace(/\/$/, '').split('?')[0] || `https://www.edenplaisir.com/content/eporner/${id}`
  
  return (
    <div className="content-page">
      <Helmet>
        <title>{pageTitle}</title>
        <link rel="canonical" href={canonicalUrl} />
        <meta name="description" content={pageDescription} />
        <meta name="robots" content="noindex, follow" />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:type" content="video.movie" />
        <meta property="og:url" content={canonicalUrl} />
        <meta name="twitter:card" content="player" />
      </Helmet>
      
      {/* Modal de gestion d'abonnement */}
      <SubscriptionModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        type={modalType}
        currentPlan={activeSubscription?.planName || activeSubscription?.plan || null}
        videoCount={videoCount}
        maxVideos={maxVideos}
        redirectContext={modalRedirectContext}
      />
      
      <div className="content-page-header">
        <h1 className="content-page-title">{videoData.title}</h1>
      </div>

      {/* Métadonnées */}
      <div className="content-page-meta">
        <div className="content-page-meta-item">
          <strong>Vues:</strong>
          <span>{videoData.views.toLocaleString()}</span>
        </div>
        {videoData.rate && (
          <div className="content-page-meta-item">
            <strong>Note:</strong>
            <span>⭐ {videoData.rate}/5.00</span>
          </div>
        )}
        <div className="content-page-meta-item">
          <strong>Durée:</strong>
          <span>{videoData.length_min}</span>
        </div>
        {videoData.added && (
          <div className="content-page-meta-item">
            <strong>Ajouté:</strong>
            <span>{formatDate(videoData.added)}</span>
          </div>
        )}
      </div>

      {/* Layout principal avec vidéo et sidebar */}
      <div className="content-page-main">
        <div className="content-page-main-content">
          {/* Affichage de la vidéo */}
          {embedSrc ? (
            <div className="content-page-video-container">
              <iframe
                src={embedSrc}
                width="100%"
                height="100%"
                allowFullScreen
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                title={videoData.title}
              />
            </div>
          ) : (
            <div className="content-page-error">
              URL de vidéo non disponible
            </div>
          )}
        </div>

        <div className="content-page-sidebar">
          {/* Tags/Keywords */}
          {tags.length > 0 && (
            <div className="content-page-tags">
              <h3>Tags</h3>
              <div className="content-page-tag-list">
                {tags.map((tag, index) => (
                  <span 
                    key={index} 
                    className="content-page-tag clickable"
                    onClick={() => navigate('/categories', { state: { filterTag: tag } })}
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Vidéos suggérées */}
      {suggestedVideos && suggestedVideos.length > 0 && (
        <div className="content-page-related">
          <h2 className="content-page-section-title">Vidéos similaires</h2>
          {isSuggestedLoading ? (
            <div className="content-page-loading">Chargement des suggestions...</div>
          ) : (
            <div className="content-page-related-grid">
              {suggestedVideos.map((video: Content) => (
                <div
                  key={video.id}
                  className="content-page-related-card"
                  onClick={() => handleSuggestedVideoClick(video)}
                  style={{ cursor: 'pointer' }}
                >
                  <div className="content-page-related-thumbnail">
                    {video.thumbnailS3Key && typeof video.thumbnailS3Key === 'string' && video.thumbnailS3Key.startsWith('http') ? (
                      <img
                        src={video.thumbnailS3Key}
                        alt={video.title}
                        onError={(e) => {
                          const target = e.target as HTMLImageElement
                          target.style.display = 'none'
                        }}
                      />
                    ) : video.thumbnailS3Key ? (
                      <img
                        src={`/api/contents/${video.id}/thumbnail`}
                        alt={video.title}
                        onError={(e) => {
                          const target = e.target as HTMLImageElement
                          target.style.display = 'none'
                        }}
                      />
                    ) : (
                      <div className="video-thumbnail placeholder" style={{ display: 'flex' }}>
                        <div className="placeholder-content">
                          <svg width="48" height="48" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M8 5v14l11-7z"/>
                          </svg>
                        </div>
                      </div>
                    )}
                    {video.duration && (
                      <div className="content-page-related-duration">
                        {typeof video.duration === 'number' 
                          ? `${Math.floor(video.duration / 60)}:${(video.duration % 60).toString().padStart(2, '0')}`
                          : video.duration}
                      </div>
                    )}
                  </div>
                  <div className="content-page-related-info">
                    <h4 className="content-page-related-title">{video.title}</h4>
                    {video.views !== undefined && (
                      <div className="content-page-related-rating">
                        {video.views.toLocaleString()} vues
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default EpornerContentPage
